import { useInsertionEffect } from 'react';
import Image from 'next/legacy/image';

import { ConditionalWrapper } from '@/hoc';
import { Container, NextLink, Text } from '@/atoms';
import { Button, WithPaperBorder } from '@/molecules';
import { Positions } from '@/molecules/with-paper-border/types';

import { containerTransformer } from '@/lib/graphql/transformers';
import { removeBaseURL } from '@/lib/utils';

const BannerEtiquetaPersonalizada = ({ bloque, id, className }: any) => {
  useInsertionEffect(() => {
    const {
      paddingExternalTop,
      paddingExternalBottom,
      paddingInternalTop,
      paddingInternalBottom,
    } = bloque.padding || {};

    let mobile_external_padding_top = paddingExternalTop || 0;
    let mobile_external_padding_bottom = paddingExternalBottom || 0;
    let mobile_internal_padding_top = paddingInternalTop || 0;
    let mobile_internal_padding_bottom = paddingInternalBottom || 0;

    if (mobile_external_padding_top === 0) {
      mobile_external_padding_top = 0;
    } else {
      mobile_external_padding_top = Math.round(
        mobile_external_padding_top * 0.5,
      );
    }

    if (mobile_external_padding_bottom === 0) {
      mobile_external_padding_bottom = 0;
    } else {
      mobile_external_padding_bottom = Math.round(
        mobile_external_padding_bottom * 0.5,
      );
    }

    if (mobile_internal_padding_top === 0) {
      mobile_internal_padding_top = 0;
    } else {
      mobile_internal_padding_top = Math.round(
        mobile_internal_padding_top * 0.5,
      );
    }

    if (mobile_internal_padding_bottom === 0) {
      mobile_internal_padding_bottom = 0;
    } else {
      mobile_internal_padding_bottom = Math.round(
        mobile_internal_padding_bottom * 0.5,
      );
    }

    const style = document.createElement('style');
    style.textContent = `
      .banner-etiqueta-personalizada-${id}-external-padding {
        padding-top: ${mobile_external_padding_top}px;
        padding-bottom: ${mobile_external_padding_bottom}px;
      }

      @media (min-width: 768px) {
        .banner-etiqueta-personalizada-${id}-external-padding {
          padding-top: ${paddingExternalTop || 0}px;
          padding-bottom: ${paddingExternalBottom || 0}px;
        }
      }
      
      .banner-etiqueta-personalizada-${id}-internal-padding {
        padding-top: ${mobile_internal_padding_top}px;
        padding-bottom: ${mobile_internal_padding_bottom}px;
      }

      @media (min-width: 768px) {
        .banner-etiqueta-personalizada-${id}-internal-padding {
          padding-top: ${paddingInternalTop || 0}px;
          padding-bottom: ${paddingInternalBottom || 0}px;
        }
      }
    `;

    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <Container
      id={id}
      as="section"
      wrapper
      wrapperSize={bloque.maxWidth}
      padding=""
      className={`banner-etiqueta-personalizada-${id}-external-padding ${className}`}
    >
      <ConditionalWrapper
        condition={
          bloque.papelRasgado.zona === Positions.top ||
          bloque.papelRasgado.zona === Positions.topBottom ||
          bloque.papelRasgado.zona === Positions.bottom
        }
        wrapper={(children) => (
          <WithPaperBorder
            top={
              bloque.papelRasgado.zona === Positions.top ||
              bloque.papelRasgado.zona === Positions.topBottom
            }
            bottom={
              bloque.papelRasgado.zona === Positions.bottom ||
              bloque.papelRasgado.zona === Positions.topBottom
            }
            border={bloque.papelRasgado.border}
            borderHeight={bloque.papelRasgado.borderHeight}
            borderColor={bloque.papelRasgado.borderColor}
          >
            {children}
          </WithPaperBorder>
        )}
      >
        <Container
          {...containerTransformer({
            align: bloque?.align,
            colorDeFondo: bloque?.colorDeFondo,
            imagenBase: bloque?.imagenBase,
            justify: bloque?.justify,
            overlay: bloque?.overlay,
            srcset: bloque?.srcset,
            textAlign: bloque?.textAlign,
          })}
          style={{ minHeight: bloque?.min_height || 0 }}
          padding="px-7 sm:px-[72px]"
          className={`banner-etiqueta-personalizada-${id}-internal-padding`}
        >
          <div className="flex flex-col">
            <div className="mb-8 flex max-w-[295px] flex-col gap-y-4 self-center sm:max-w-[400px]">
              {bloque.pretitulo && (
                <div className="u-label u-label--s font-bold text-secondary-500">
                  {bloque.pretitulo}
                </div>
              )}

              {bloque.titulo && (
                <Text
                  className="u-headline u-headline--h1"
                  as={bloque.headingType}
                >
                  {bloque.titulo}
                </Text>
              )}

              {bloque?.description && (
                <div
                  className="u-body u-body--s font-medium text-primary-600"
                  dangerouslySetInnerHTML={{ __html: bloque.description }}
                />
              )}
            </div>

            {bloque.imagen?.sourceUrl && (
              <Image
                src={bloque.imagen.sourceUrl}
                width={bloque.imagen.mediaDetails.width}
                height={bloque.imagen.mediaDetails.height}
                alt={bloque.imagen.altText}
              />
            )}

            {bloque?.label && bloque?.link?.url && (
              <NextLink href={removeBaseURL(bloque.link.url)} passHref>
                <Button
                  as="a"
                  variant={bloque.style}
                  className="min-w-full sm:mx-auto sm:min-w-0"
                  negative={bloque.negativo}
                >
                  {bloque.label}
                </Button>
              </NextLink>
            )}
          </div>
        </Container>
      </ConditionalWrapper>
    </Container>
  );
};

export default BannerEtiquetaPersonalizada;

BannerEtiquetaPersonalizada.displayName = 'FlexibleBannerEtiquetaPersonalizada';
